import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { Project, Site, from, is } from "@the-real-insight/tri-model";
import { userAPI } from "../UserAPI";
import { authenticationAtom } from "../State";
import { navigableEntityId } from "./entityNavigation";
import {
  TRAIL_TYPES,
  buildEntityTrail,
  directParentIds,
  siteIdsNamingBuilding,
  type TrailItem,
  type TrailParts,
  type TrailType,
} from "./entityTrail";

const isTrailType = (type: string | null | undefined): type is TrailType =>
  !!type && (TRAIL_TYPES as readonly string[]).includes(type);

const byId = async (type: string, id: string | undefined): Promise<any | undefined> => {
  if (!id) return undefined;
  try {
    return (await userAPI.getEntityByID(type, id)) ?? undefined;
  } catch {
    return undefined;
  }
};

/**
 * Das Projekt eines Gebäudes — nur wenn es genau eines gibt.
 *
 * Ein Gebäude hängt über die Liegenschaft am Projekt (`Project.sites` → `Site.buildings`). Steht es
 * in mehreren Projekten, wäre jedes davon geraten; dann fehlt das Glied lieber.
 *
 * Die Liegenschaften werden je Mandant geladen und hier gefiltert: Ein Filter auf `Site.buildings`
 * castet der Server zu ObjectId, gespeichert sind die Ids als String — er fand nie etwas.
 */
const projectOfBuilding = async (
  buildingId: string | undefined,
  customer: string | undefined,
): Promise<any | undefined> => {
  if (!buildingId || !customer) return undefined;
  try {
    const sites = await userAPI.query(from(Site.type).where(is("_tenant").equal(customer)));
    const siteIds = siteIdsNamingBuilding(Array.isArray(sites) ? sites : [], buildingId);
    if (siteIds.length === 0) return undefined;
    const projects = await userAPI.query(from(Project.type).where(is("sites").in(siteIds) as any));
    return Array.isArray(projects) && projects.length === 1 ? projects[0] : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Löst den Pfad über einer Entitätsseite auf (Projekt › Gebäude › Etage › Raum).
 *
 * Gelesen wird von unten nach oben über die Ids, die jede Ebene selbst trägt; was nicht zu finden
 * ist, fehlt im Pfad, statt die Seite zu blockieren.
 */
export function useEntityTrail(entityType: string | null | undefined, entity: any): TrailItem[] {
  const { t } = useTranslation();
  const [authentication] = useAtom(authenticationAtom) as any;
  const customer: string | undefined = authentication?.customer;
  const [parts, setParts] = useState<TrailParts>({});
  const entityId = navigableEntityId(entity);

  useEffect(() => {
    if (!isTrailType(entityType) || !entity) {
      setParts({});
      return;
    }
    let cancelled = false;
    const own: TrailParts = { [entityType.toLowerCase()]: entity } as TrailParts;
    // Die Seite selbst sofort, die Ebenen darüber, sobald sie da sind.
    setParts(own);

    (async () => {
      const ids = directParentIds(entityType, entity);
      const space = entityType === "Space" ? entity : await byId("Space", ids.space);
      const floorId = ids.floor ?? navigableEntityId(space?.floor);
      const floor = entityType === "Floor" ? entity : await byId("Floor", floorId);
      const buildingId = ids.building ?? navigableEntityId(floor?.building);
      const building = entityType === "Building" ? entity : await byId("Building", buildingId);
      const project = await projectOfBuilding(navigableEntityId(building) ?? buildingId, customer);
      if (!cancelled) setParts({ ...own, project, building, floor, space });
    })();

    return () => {
      cancelled = true;
    };
    // Neu auflösen, wenn eine andere Entität geöffnet wird — nicht bei jeder Änderung an ihr.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId, customer]);

  if (!isTrailType(entityType)) return [];
  return buildEntityTrail(parts, entityType, (type) => t(`entityPage.types.${type}`));
}
